import { useEffect, useState } from 'react';

// material-ui
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import useAuth from 'hooks/useAuth';
import { notifyAttendanceChanged } from 'hooks/useAttendanceRefresh';
import { formatDateDisplay } from 'utils/formatDate';
import { listMyLeavePeriods, recordContractorNotice } from 'api/attendance';

// assets
import { IconCalendar, IconExpandMore, IconWarningTriangle } from 'utils/icons';

// ==============================|| PROFILE - NAHLÁSIT VOLNO (EXTERNÍ DODAVATEL) ||============================== //
// 2026-08-30, na výslovné přání uživatele - externí dodavatel appku
// nepoužívá jako zaměstnanec (nepíchá, nebere propustky, nežádá o
// dovolenou přes schvalovací cyklus) - jen NAHLÁSÍ, kdy si bere volno, aby
// o tom appka (a kdo si seznam kontroluje) věděla. Appka nic neschvaluje
// ani neblokuje - doporučených 14 dní dopředu je jen měkké upozornění,
// pozdější nahlášení appka jen označí trojúhelníkem (needs_review), stejný
// "signály nikdy neblokují" princip jako zbytek Docházky.
//
// Renderuje se MÍSTO AttendanceAccordion (viz Profile/index.jsx's branching
// na skupinu "externí dodavatelé") - dodavatel nepotřebuje ani nemá vidět
// zbytek Docházky, proto samostatný, mnohem menší komponent, ne SubAccordion
// uvnitř AttendanceAccordion.
//
// Vlastní gate uvnitř komponenty (stejná přímá skupina "externí dodavatelé",
// core_group_members) je vědomá duplicita s branchingem v Profile/index.jsx -
// stejný "obrana do hloubky" vzor jako AttendanceAccordion's vlastní
// `canSeeAttendance` check.

const CONTRACTOR_NOTICE_MIN_DAYS = 14;

function daysFromToday(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  return Math.round((target - today) / 86400000);
}

export default function ContractorNoticeAccordion({ expanded: sectionExpanded, onChange: onSectionChange }) {
  const { user } = useAuth();
  const isContractor = (user?.groups || []).some((g) => (g.name || '').trim().toLowerCase() === 'externí dodavatelé');

  const [notices, setNotices] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = () => {
    listMyLeavePeriods()
      .then((rows) => setNotices(rows.filter((r) => r.leave_type === 'contractor_notice')))
      .catch(() => {});
  };

  useEffect(() => {
    if (!isContractor) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContractor]);

  if (!isContractor) return null;

  const notice = daysFromToday(startDate);
  const showLateWarning = notice !== null && notice >= 0 && notice < CONTRACTOR_NOTICE_MIN_DAYS;

  const handleSubmit = async () => {
    if (!startDate || !endDate) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await recordContractorNotice(startDate, endDate, undefined);
      setSuccess('Volno bylo nahlášeno.');
      setStartDate('');
      setEndDate('');
      notifyAttendanceChanged();
      load();
    } catch (e) {
      setError(e.message || 'Nahlášení se nepodařilo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      expanded={sectionExpanded}
      onChange={onSectionChange}
      sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}
    >
      <AccordionSummary expandIcon={<IconExpandMore style={{ fontSize: '1.05rem' }} />} sx={{ px: 2.5 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <IconCalendar style={{ fontSize: '1.3rem' }} />
          <Typography variant="subtitle1">Nahlásit volno</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2.5, pt: 1.25, pb: 1.5 }}>
        <Stack sx={{ gap: 1.25 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            Jakékoliv plánované volno nebo přerušení služeb je nutné hlásit s dostatečným předstihem (min. {CONTRACTOR_NOTICE_MIN_DAYS}{' '}
            dnů), pokud je to možné, podle smluvních podmínek.
          </Typography>
          <TextField
            type="date"
            label="Od"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="date"
            label="Do"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {showLateWarning && (
            <Alert severity="warning">
              Nahlašujete volno na méně než {CONTRACTOR_NOTICE_MIN_DAYS} dní dopředu - příště to prosím zkuste dřív, pokud to situace
              dovolí.
            </Alert>
          )}
          <Button fullWidth size="small" variant="contained" disabled={busy || !startDate || !endDate} onClick={handleSubmit} sx={{ textTransform: 'none' }}>
            {busy ? <CircularProgress size={16} /> : 'Nahlásit'}
          </Button>
          {error && (
            <Typography variant="body2" sx={{ color: 'error.main' }}>
              {error}
            </Typography>
          )}
          {!error && success && (
            <Typography variant="body2" sx={{ color: 'success.main' }}>
              {success}
            </Typography>
          )}

          {notices.length > 0 && (
            <Stack sx={{ gap: 0.75, mt: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Moje nahlášená volna
              </Typography>
              {notices.map((n) => (
                <Stack key={n.id} direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
                  <Typography variant="caption">
                    {formatDateDisplay(n.start_date)}
                    {n.end_date && n.end_date !== n.start_date ? ` - ${formatDateDisplay(n.end_date)}` : ''}
                  </Typography>
                  {!!Number(n.needs_review) && (
                    <Tooltip title={`Hlášeno méně než ${CONTRACTOR_NOTICE_MIN_DAYS} dní dopředu`}>
                      <IconWarningTriangle color="warning" style={{ fontSize: '1rem' }} />
                    </Tooltip>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
